<template>
  <div class="user-annotation-terms-panel">
    <div v-if="userOntologies.length" class="ontology-tags field is-grouped is-grouped-multiline mb-3">
      <div class="control" v-for="ontology in userOntologies" :key="ontology.id">
        <div class="tags has-addons">
          <span class="tag is-info">{{ ontology.name }}</span>
          <a
            v-if="canManageProject"
            class="tag is-delete"
            :title="$t('remove-terms-from-image')"
            @click="removeOntologyFromImage(ontology.id)"
          ></a>
        </div>
      </div>
    </div>

    <div class="term-list">
      <div v-for="term in termRows" :key="term.id" class="term-group">
        <div
          class="term-header"
          role="button"
          tabindex="0"
          @click="toggleTerm(term)"
          @keydown.enter.prevent="toggleTerm(term)"
          @keydown.space.prevent="toggleTerm(term)"
        >
          <span class="term-main">
            <span class="arrow" :class="{ open: termState(term).expanded }">▶</span>
            <label v-if="term.id" class="switch is-small" @click.stop>
              <input
                type="checkbox"
                :checked="getTermVisibility(term)"
                @change="toggleTermVisibility(term)"
              >
              <span class="switch-slider"></span>
            </label>
            <span class="dot" :style="{ background: term.color }"></span>
            <span class="term-label">{{ term.name }}</span>
          </span>
        </div>

        <div v-if="termState(term).expanded" class="annotation-list">
          <b-loading :is-full-page="false" class="small" :active="termState(term).loading" />
          <template v-if="termState(term).error">
            <b-message type="is-danger" has-icon icon-size="is-small">
              {{ $t('failed-to-load-annotations') }}
            </b-message>
          </template>
          <template v-else-if="!termState(term).items.length && !termState(term).loading">
            <em class="empty-message">{{ $t('no-annotation') }}</em>
          </template>
          <div v-else class="annotation-previews">
            <annotation-preview
              v-for="annot in termState(term).items"
              :key="annot.id"
              class="annotation-preview"
              :annot="annot"
              :size="72"
              :color="term.colorHex"
              :terms="terms"
              :images="images"
              :show-image-info="false"
              :same-view-on-click="true"
              @updateTermsOrTracks="$emit('updateTermsOrTracks', annot)"
              @centerView="$emit('centerView', { annot, sameView: true })"
              @deletion="$emit('delete', annot)"
              @select="$emit('select', $event)"
            />
          </div>
          <button
            v-if="termState(term).hasMore"
            class="button is-small is-dark load-more"
            :disabled="termState(term).loading"
            @click.stop="loadAnnotations(term, true)"
          >
            {{ $t('button-load-more') }}
          </button>
        </div>
      </div>
    </div>

    <div class="has-text-right mt-2">
      <button class="button is-small is-link" @click="$emit('open-add-terms')">{{ $t('add-terms') }}</button>
    </div>
  </div>
</template>

<script>
import { Annotation } from '@/api';
import Cytomine from '@/api/cytomine';
import AnnotationPreview from '@/components/annotations/AnnotationPreview';
import viewerImageContext from './mixins/viewer-image-context';
import { getAllTerms } from '@/utils/ontology-utils';

const NO_TERM_ID = 0;

export default {
  name: 'user-annotation-terms-panel',
  mixins: [viewerImageContext],
  components: {
    AnnotationPreview
  },
  props: {
    index: String,
    project: {
      type: Object,
      default: null
    },
    algorithmOntologyIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      pageSize: 24,
      annotationContextVersion: 0,
      states: {}
    };
  },
  computed: {
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    images() {
      return this.image ? [this.image] : [];
    },
    imageId() {
      return this.image?.id;
    },
    ontologyTerms() {
      return this.imageWrapper.style.ontologyTerms;
    },
    userOntologies() {
      return (this.imageWrapper.ontologies || [])
        .filter(ontology => !this.isAlgorithmOntology(ontology));
    },
    terms() {
      return this.userOntologies.flatMap(ontology => {
        if (!ontology || !ontology.children || !Array.isArray(ontology.children)) {
          return [];
        }

        return getAllTerms(ontology).map(term => ({
          ...term,
          ontologyId: ontology.id
        }));
      });
    },
    termRows() {
      const terms = this.terms
        .filter(term => !this.hiddenTermsIds.includes(term.id))
        .map(term => ({
          ...term,
          color: term.color || '#9575CD',
          colorHex: (term.color || '#9575CD').replace(/^#/, ''),
          noTerm: false
        }));

      return [
        ...terms,
        {
          id: NO_TERM_ID,
          name: this.$t('no-term'),
          color: '#9CA3AF',
          colorHex: '9CA3AF',
          noTerm: true
        }
      ];
    },
    hiddenTermsIds() {
      return this.$store.getters[this.imageModule + 'hiddenTermsIds'] || [];
    }
  },
  watch: {
    imageId(newImageId, oldImageId) {
      if (newImageId && newImageId !== oldImageId) {
        this.annotationContextVersion++;
        this.states = {};
      }
    }
  },
  methods: {
    isAlgorithmOntology(ontology) {
      return this.algorithmOntologyIds.includes(ontology.id) || /\s\(AI\)$/.test(ontology.name || '');
    },
    stateKey(term) {
      return term.noTerm ? 'no-term' : String(term.id);
    },
    termState(term) {
      const key = this.stateKey(term);
      if (!this.states[key]) {
        this.$set(this.states, key, {
          expanded: false,
          loading: false,
          error: false,
          items: [],
          page: 0,
          hasMore: false,
          requestId: 0,
          term
        });
      }
      return this.states[key];
    },
    async toggleTerm(term) {
      const state = this.termState(term);
      state.expanded = !state.expanded;

      if (state.expanded && !state.items.length && !state.loading) {
        await this.loadAnnotations(term);
      }
    },
    getTermVisibility(term) {
      if (this.ontologyTerms && this.ontologyTerms[term.id]) {
        return this.ontologyTerms[term.id].visible;
      }
      return true;
    },
    toggleTermVisibility(term) {
      this.$store.commit(this.imageModule + 'toggleOntologyTermVisibility', { termId: term.id });
    },
    async removeOntologyFromImage(ontologyId) {
      if (!confirm('Are you sure you want to remove these terms from the image?')) {
        return;
      }

      try {
        await this.$store.dispatch(this.imageModule + 'removeOntologyFromImage', ontologyId);
        this.$notify({ type: 'success', text: this.$t('notif-success-remove-ontology-from-image') });
      } catch (error) {
        console.error('Error removing ontology from image:', error);
        this.$notify({ type: 'error', text: this.$t('notif-error-remove-ontology-from-image') });
      }
    },
    normalizeAnnotation(raw) {
      const annot = raw instanceof Annotation ? raw : new Annotation(raw);
      const basePath = `${Cytomine.instance.host}${Cytomine.instance.basePath}`;
      annot.cropURL = annot.cropURL || `${basePath}userannotation/${annot.id}/crop.png`;
      annot.smallCropURL = annot.smallCropURL || `${basePath}userannotation/${annot.id}/crop.png?maxSize=256`;
      return annot;
    },
    async loadAnnotations(term, append = false) {
      const state = this.termState(term);
      const nextPage = append ? state.page + 1 : 0;
      const contextVersion = this.annotationContextVersion;
      const requestId = state.requestId + 1;
      const imageId = this.imageId;
      state.requestId = requestId;

      state.loading = true;
      state.error = false;

      try {
        if (!imageId || !this.project || !this.project.id) {
          throw new Error('Cannot load user annotations without image and project context.');
        }

        const params = {
          images: String(imageId),
          max: this.pageSize,
          multipleTerm: false,
          noTerm: term.noTerm,
          offset: nextPage * this.pageSize,
          project: this.project.id,
          reviewed: false,
          showAlgo: false,
          showDefault: true,
          showGIS: true,
          showImage: false,
          showImageGroup: true,
          showTerm: true,
          showWKT: true
        };

        if (!term.noTerm) {
          params.term = term.id;
        }

        const { data } = await Cytomine.instance.api.post('annotation/search.json', params);
        if (contextVersion !== this.annotationContextVersion || requestId !== state.requestId || imageId !== this.imageId) {
          return;
        }

        const items = (data.collection || []).map(raw => this.normalizeAnnotation(raw));
        state.items = append ? [...state.items, ...items] : items;
        state.page = nextPage;
        state.hasMore = data.totalPages ? nextPage + 1 < data.totalPages : items.length === this.pageSize;
      } catch (error) {
        if (contextVersion !== this.annotationContextVersion || requestId !== state.requestId || imageId !== this.imageId) {
          return;
        }
        console.error('Failed to load user annotations:', error);
        state.error = true;
        state.hasMore = false;
      } finally {
        if (contextVersion === this.annotationContextVersion && requestId === state.requestId && imageId === this.imageId) {
          state.loading = false;
        }
      }
    },
    refreshAnnotations(payload) {
      const idImage = payload && typeof payload === 'object' ? (payload.idImage || payload.image) : payload;
      if (!this.image || (idImage !== undefined && idImage !== null && idImage !== this.image.id)) {
        return;
      }

      Object.values(this.states).forEach(state => {
        if (state.expanded) {
          state.items = [];
          state.page = 0;
          state.hasMore = false;
          this.loadAnnotations(state.term);
        }
      });
    }
  },
  mounted() {
    this.$eventBus.$on('addAnnotation', this.refreshAnnotations);
    this.$eventBus.$on('reloadAnnotations', this.refreshAnnotations);
    this.$eventBus.$on('editAnnotation', this.refreshAnnotations);
    this.$eventBus.$on('deleteAnnotation', this.refreshAnnotations);
  },
  beforeDestroy() {
    this.$eventBus.$off('addAnnotation', this.refreshAnnotations);
    this.$eventBus.$off('reloadAnnotations', this.refreshAnnotations);
    this.$eventBus.$off('editAnnotation', this.refreshAnnotations);
    this.$eventBus.$off('deleteAnnotation', this.refreshAnnotations);
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.user-annotation-terms-panel {
  color: $dark-text-primary;
}

.term-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.term-group {
  overflow: hidden;
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 8px;
  background: rgba(222, 238, 255, 0.032);
}

.term-header {
  width: 100%;
  min-height: 2.7rem;
  padding: 0.45rem 0.6rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 0;
  background: transparent;
  color: $dark-text-primary;
  cursor: pointer;
  text-align: left;
}

.term-header:hover {
  background: rgba($primary, 0.07);
}

.term-main {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  font-weight: 600;
}

.term-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.arrow {
  color: $dark-text-disabled;
  transition: transform 0.18s ease, color 0.18s ease;
}

.arrow.open {
  transform: rotate(90deg);
  color: $primary;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  min-width: 12px;
  min-height: 12px;
}

.switch {
  position: relative;
  width: 42px;
  height: 22px;
  display: inline-block;
  flex: 0 0 auto;
}

.switch input {
  display: none;
}

.switch-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(132, 156, 184, 0.16);
  border: 1px solid rgba(190, 214, 238, 0.15);
  border-radius: 20px;
  transition: 0.2s;
}

.switch-slider:before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 1px;
  background: $dark-text-secondary;
  border-radius: 50%;
  transition: 0.2s;
}

input:checked + .switch-slider {
  background: $primary;
  border-color: $primary;
}

input:checked + .switch-slider:before {
  transform: translateX(19px);
}

.switch.is-small {
  width: 34px;
  height: 18px;
}

.switch.is-small .switch-slider:before {
  width: 14px;
  height: 14px;
}

.switch.is-small input:checked + .switch-slider:before {
  transform: translateX(16px);
}

.annotation-list {
  position: relative;
  min-height: 3rem;
  padding: 0.65rem 0.8rem;
  border-top: 1px solid rgba(190, 214, 238, 0.1);
  background: rgba(8, 17, 29, 0.28);
}

.annotation-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.annotation-preview {
  margin: 0;
}

.load-more {
  margin-top: 0.65rem;
}

.empty-message {
  color: $dark-text-secondary;
  font-size: 0.84rem;
}
</style>
